<template>
  <div class="delete-request-review">
    <div class="lvyun-profile-banner">
      <h2>审核申请</h2>
      <p>集中审核删除、退款、取消订单等申请。</p>
    </div>

    <el-card class="review-card" shadow="never">
      <template slot="header">
        <div class="card-header">
          <div class="status-tabs">
            <button
              v-for="tab in statusTabs"
              :key="tab.value"
              type="button"
              class="status-tab"
              :class="{ active: statusFilter === tab.value }"
              @click="setStatusFilter(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
          <el-button size="small" class="btn-secondary" :loading="loading" @click="loadDeleteRequests">
            刷新
          </el-button>
        </div>
      </template>

      <el-table
        :data="deleteRequests"
        style="width: 100%"
        stripe
        class="review-table"
        :row-class-name="deleteRequestRowClassName"
        empty-text="暂无申请记录"
      >
        <el-table-column prop="id" label="申请ID" width="90" />
        <el-table-column label="目标" min-width="170">
          <template slot-scope="scope">
            <div>{{ targetTypeLabel(scope.row.target_type) }}</div>
            <div class="delete-request-subtext">{{ scope.row.target_label || scope.row.target_id }}</div>
          </template>
        </el-table-column>
        <el-table-column label="申请人" min-width="150">
          <template slot-scope="scope">
            <div>{{ scope.row.requested_by_name || '—' }}</div>
            <div class="delete-request-subtext">{{ scope.row.requested_by_role || '—' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="reason" label="申请原因" min-width="140" show-overflow-tooltip />
        <el-table-column prop="created_at" label="申请时间" width="170">
          <template slot-scope="scope">{{ $formatDateTime(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <span class="status-pill" :class="'st-' + String(scope.row.status || '')">
              {{ deleteRequestStatusText(scope.row.status) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="审核信息" min-width="160">
          <template slot-scope="scope">
            <template v-if="String(scope.row.status) !== 'pending'">
              <div>{{ scope.row.reviewed_by_name || '—' }}</div>
              <div class="delete-request-subtext">
                {{ scope.row.reviewed_at ? $formatDateTime(scope.row.reviewed_at) : '—' }}
              </div>
              <div v-if="scope.row.review_note" class="delete-request-subtext">
                {{ scope.row.review_note }}
              </div>
            </template>
            <span v-else class="delete-request-subtext">—</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="148" align="center">
          <template slot-scope="scope">
            <div v-if="String(scope.row.status) === 'pending'" class="op-actions">
              <button
                type="button"
                class="op-btn op-approve"
                @click="reviewDeleteRequest(scope.row, 'approve')"
              >
                {{ approveButtonText(scope.row) }}
              </button>
              <button
                type="button"
                class="op-btn op-reject"
                @click="reviewDeleteRequest(scope.row, 'reject')"
              >
                驳回
              </button>
            </div>
            <span v-else class="op-done">已处理</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { getUserInfo } from '../utils/auth'

export default {
  name: 'DeleteRequestReview',
  data() {
    return {
      deleteRequests: [],
      loading: false,
      focusedDeleteRequestId: null,
      statusFilter: 'all',
      statusTabs: [
        { label: '全部', value: 'all' },
        { label: '待审核', value: 'pending' },
        { label: '已通过', value: 'approved' },
        { label: '已驳回', value: 'rejected' }
      ]
    }
  },
  computed: {
    canReviewDeleteRequestsSession() {
      const u = getUserInfo()
      const r = String((u && u.roleLabel) || '').trim()
      return r === '管理员' || r === '超级管理员'
    }
  },
  mounted() {
    const qid = this.$route && this.$route.query ? this.$route.query.deleteRequestId : ''
    this.focusedDeleteRequestId = qid ? Number(qid) : null
    if (qid) this.statusFilter = 'all'
    this.loadDeleteRequests()
  },
  watch: {
    '$route.query.deleteRequestId': {
      immediate: false,
      handler(v) {
        this.focusedDeleteRequestId = v ? Number(v) : null
        if (v) this.statusFilter = 'all'
        this.loadDeleteRequests()
      }
    }
  },
  methods: {
    setStatusFilter(value) {
      this.statusFilter = value
      this.loadDeleteRequests()
    },
    async loadDeleteRequests() {
      if (!this.canReviewDeleteRequestsSession) {
        this.$message.error('仅管理员或超级管理员可查看审核申请')
        return
      }
      this.loading = true
      try {
        const params = { status: this.statusFilter || 'all' }
        const resp = await this.$api.get('/admin/delete-requests', { params })
        if (resp && resp.code === 200 && Array.isArray(resp.data)) {
          this.deleteRequests = resp.data
          if (this.focusedDeleteRequestId) {
            const hit = this.deleteRequests.some((x) => Number(x.id) === Number(this.focusedDeleteRequestId))
            this.$nextTick(() => {
              if (hit) this.$message.success(`已定位到申请 #${this.focusedDeleteRequestId}`)
              else this.$message.info(`未找到申请 #${this.focusedDeleteRequestId}`)
            })
          }
        } else {
          this.deleteRequests = []
        }
      } catch (err) {
        this.deleteRequests = []
        const msg =
          (err && err.response && err.response.data && err.response.data.message) || '审核申请加载失败'
        this.$message.error(msg)
      } finally {
        this.loading = false
      }
    },
    targetTypeLabel(t) {
      const x = String(t || '').trim()
      if (x === 'order') return '订单删除'
      if (x === 'order_refund') return '退款申请'
      if (x === 'order_cancel') return '取消订单申请'
      if (x === 'subscription_refund') return '随心订退款申请'
      if (x === 'subscription_cancel') return '随心订取消申请'
      if (x === 'voucher') return '兑换券删除'
      if (x === 'points_goods') return '积分商品删除'
      return x || '未知类型'
    },
    approveButtonText(row) {
      const x = String((row && row.target_type) || '').trim()
      if (x === 'order_refund' || x === 'subscription_refund') return '通过并退款'
      if (x === 'order_cancel' || x === 'subscription_cancel') return '通过并取消'
      return '通过并删除'
    },
    approveConfirmText(row) {
      const x = String((row && row.target_type) || '').trim()
      if (x === 'order_refund' || x === 'subscription_refund') {
        return '确认通过该退款申请并执行退款？'
      }
      if (x === 'order_cancel' || x === 'subscription_cancel') {
        return '确认通过该取消申请并取消订单？'
      }
      return '确认通过该删除申请并立即执行删除？'
    },
    deleteRequestStatusText(s) {
      const x = String(s || '').trim()
      if (x === 'pending') return '待审核'
      if (x === 'approved') return '已通过'
      if (x === 'rejected') return '已驳回'
      return x || '未知'
    },
    deleteRequestRowClassName({ row }) {
      if (this.focusedDeleteRequestId && Number(row.id) === Number(this.focusedDeleteRequestId)) {
        return 'delete-request-row-focus'
      }
      return ''
    },
    async reviewDeleteRequest(row, action) {
      const id = row && row.id
      if (!id) return
      const isApprove = action === 'approve'
      try {
        await this.$confirm(
          isApprove ? this.approveConfirmText(row) : '确认驳回该申请？',
          isApprove ? '通过申请' : '驳回申请',
          { type: isApprove ? 'warning' : 'info' }
        )
      } catch (_e) {
        return
      }
      try {
        const resp = await this.$api.put(`/admin/delete-requests/${id}/review`, { action, note: '' })
        if (resp && resp.code === 200) {
          this.$message.success((resp && resp.message) || '处理成功')
          this.statusFilter = 'all'
          await this.loadDeleteRequests()
        } else {
          this.$message.error((resp && resp.message) || '处理失败')
        }
      } catch (err) {
        const msg = (err && err.response && err.response.data && err.response.data.message) || '处理失败'
        this.$message.error(msg)
      }
    }
  }
}
</script>

<style scoped>
.delete-request-review {
  padding: 24px;
  background: transparent;
}

.review-card {
  border-radius: 12px;
  box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.06);
  border: none;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.status-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.status-tab {
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: none;
  outline: none;
}

.status-tab:hover {
  border-color: #cbd5e1;
  color: #334155;
}

.status-tab.active {
  background: #ecfdf5;
  border-color: #86efac;
  color: #047857;
}

.delete-request-subtext {
  color: #86909c;
  font-size: 12px;
  margin-top: 2px;
  line-height: 1.4;
}

.status-pill {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 6px;
}

.status-pill.st-pending {
  background: #fff7ed;
  color: #c2410c;
}

.status-pill.st-approved {
  background: #ecfdf5;
  color: #047857;
}

.status-pill.st-rejected {
  background: #f1f5f9;
  color: #64748b;
}

.op-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
}

.op-btn {
  appearance: none;
  margin: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  padding: 8px 10px;
  cursor: pointer;
  box-shadow: none !important;
  outline: none;
  text-shadow: none;
  filter: none;
  transform: none;
}

.op-btn:hover,
.op-btn:focus,
.op-btn:active {
  box-shadow: none !important;
  filter: none;
  transform: none;
  outline: none;
}

.op-approve {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}

.op-approve:hover {
  background: #d1fae5;
  border-color: #6ee7b7;
}

.op-reject {
  background: #fff1f2;
  border-color: #fecdd3;
  color: #be123c;
}

.op-reject:hover {
  background: #ffe4e6;
  border-color: #fda4af;
}

.op-done {
  color: #94a3b8;
  font-size: 13px;
}

.review-table >>> .delete-request-row-focus > td {
  background: #ecf5ff !important;
}

.review-table >>> .el-button {
  box-shadow: none !important;
}
</style>
